/**
 * Represents a deferred promise with exposed resolve and reject functions.
 *
 * @template T The type of the value that the promise resolves to.
 */
export type DeferredPromise<T> = {
    /**
     * The underlying Promise object.
     */
    promise: Promise<T>;

    /**
     * Function to resolve the promise with a value of type T.
     * @param arg The value to resolve the promise with.
     */
    resolve: (arg: T) => void;

    /**
     * Function to reject the promise with a reason.
     * @param arg The reason for rejecting the promise.
     */
    reject: (arg: unknown) => void;
};

/**
 * Creates a deferred promise, allowing external control over its resolution or rejection.
 *
 * This function returns an object containing a promise and methods to resolve or reject it,
 * enabling more flexible promise handling in asynchronous operations.
 *
 * @template T The type of the value that the promise will resolve to.
 * @returns {DeferredPromise<T>} An object containing the promise and its control functions.
 *
 * @example
 * // Create a deferred promise
 * const deferred = createDeferredPromise<string>();
 *
 * // Use the promise
 * deferred.promise.then(value => console.log(value)).catch(err => console.error(err));
 *
 * // Resolve the promise later
 * setTimeout(() => {
 *   deferred.resolve('Hello, World!');
 * }, 1000);
 *
 * @example
 * // Using in an async context
 * async function fetchWithTimeout(url: string, timeout: number): Promise<Response> {
 *   const deferred = createDeferredPromise<Response>();
 *
 *   const fetchPromise = fetch(url).then(deferred.resolve).catch(deferred.reject);
 *   const timeoutPromise = new Promise((_, reject) =>
 *     setTimeout(() => reject(new Error('Request timed out')), timeout)
 *   );
 *
 *   return Promise.race([deferred.promise, timeoutPromise]);
 * }
 */
export function createDeferredPromise<T>(): DeferredPromise<T> {
    const deferred: Partial<DeferredPromise<T>> = {};
    deferred.promise = new Promise((resolve, reject) => {
        deferred.resolve = resolve;
        deferred.reject = reject;
    });
    return deferred as DeferredPromise<T>;
}
